import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import moment from "moment";
import DataTableComponentFrontPagination from "../../common/DataTableComponentFrontPagination";
import StandardModal from "../../common/Modal/StandardModal";
import ResizableTextarea from "../../library/ResizableTextarea";
import { AcknowledgeFirstAidTagMismatch } from "../../../services/FirstAidBoxService";
import { invalidateFirstAidBoxQueries } from "../../../query/hooks/QueryHooksFirstAidBox";
import { EVENT_TYPE_LABELS, OHS_BASE_PATH } from "./ohsConstants";
import { getOhsErrorMessage } from "./ohsUtils";
import { ConditionBadge, MutedText, SectionCard, SectionHeader, SectionTitle } from "./Ohs.styles";

/**
 * Tag mismatches awaiting a manager - someone opened a box and the tamper tag they found wasn't the
 * one on record. Each stays here until a manager acknowledges it with a note.
 * @param {Object} props
 * @param {Array<Object>} props.tagMismatches useFirstAidManagerOverview().tagMismatches
 */
function TagMismatches({ tagMismatches }) {
    const [search, setSearch] = useState("");
    const [acknowledgingMismatch, setAcknowledgingMismatch] = useState(null);
    const [note, setNote] = useState("");
    const [noteError, setNoteError] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const filteredMismatches = useMemo(() => {
        const searchText = search.trim().toLowerCase();
        if (!searchText) {
            return tagMismatches;
        }
        return tagMismatches.filter((tagMismatch) => {
            return tagMismatch.box_code.toLowerCase().includes(searchText)
                || (tagMismatch.opened_by_name || "").toLowerCase().includes(searchText)
                || (tagMismatch.campus_name || "").toLowerCase().includes(searchText);
        });
    }, [tagMismatches, search]);

    const closeModal = () => {
        setAcknowledgingMismatch(null);
        setNote("");
        setNoteError("");
    };

    const handleAcknowledge = async () => {
        if (!note.trim()) {
            setNoteError("Add a note on what you found out");
            return;
        }

        setIsSaving(true);
        try {
            await AcknowledgeFirstAidTagMismatch(acknowledgingMismatch.id, note.trim());
            await invalidateFirstAidBoxQueries();
            closeModal();
        } catch (error) {
            console.log("TagMismatches.js->handleAcknowledge->Error", error);
            Swal.fire("Error", getOhsErrorMessage(error, "The tag mismatch could not be acknowledged."), "error");
        } finally {
            setIsSaving(false);
        }
    };

    const columns = [
        {
            name: "Box",
            selector: "box_code",
            sortable: true,
            minWidth: "170px",
            cell: (row) => (
                <div>
                    <Link to={`${OHS_BASE_PATH}/boxes/open/${row.box_id}`}>{row.box_code}</Link>
                    <div><MutedText>{row.campus_name} · {row.area_location}</MutedText></div>
                </div>
            ),
        },
        {
            name: "Tag Found",
            selector: "tag_serial_found",
            minWidth: "130px",
            // A null tag found means the person reported no tag on the box at all.
            cell: (row) => row.tag_serial_found || <ConditionBadge $condition="damaged">No Tag</ConditionBadge>,
        },
        {
            name: "Tag On Record",
            selector: "tag_serial_on_record",
            minWidth: "190px",
            cell: (row) => (
                <div>
                    <div>{row.tag_serial_on_record}</div>
                    <MutedText>
                        {row.tag_on_record_fitted_at
                            ? `Fitted ${moment(row.tag_on_record_fitted_at).format("D MMM YYYY")} · on the box ${moment(row.created_at).diff(moment(row.tag_on_record_fitted_at), "days")} days`
                            : "Entered on the box page - no fitting date"}
                    </MutedText>
                </div>
            ),
        },
        {
            name: "New Tag",
            selector: "new_tag_serial",
            minWidth: "120px",
            cell: (row) => row.new_tag_serial || "Not fitted yet",
        },
        {
            name: "Opened",
            selector: "created_at",
            sortable: true,
            minWidth: "200px",
            cell: (row) => (
                <div>
                    <div>{row.opened_by_name || "-"}</div>
                    <MutedText>{EVENT_TYPE_LABELS[row.event_type]} · {moment(row.created_at).format("D MMM YYYY HH:mm")}</MutedText>
                </div>
            ),
        },
        {
            name: "Action",
            width: "90px",
            right: true,
            cell: (row) => (
                <div className="assessment-08 btn-dropdown-grp">
                    <div className="as-buttons">
                        <button
                            type="button"
                            className="btn btn-primary rounded-circle"
                            title="Acknowledge"
                            onClick={() => setAcknowledgingMismatch(row)}
                        >
                            <i className="fal fa-check"></i>
                        </button>
                    </div>
                </div>
            ),
        },
    ];

    return (
        <SectionCard>
            <SectionHeader>
                <div>
                    <SectionTitle>Tag Mismatches</SectionTitle>
                    <MutedText>Boxes opened where the tamper tag found wasn&apos;t the one on record. Acknowledge each once you know why.</MutedText>
                </div>
            </SectionHeader>
            <DataTableComponentFrontPagination
                data={filteredMismatches}
                columns={columns}
                keyField="id"
                search={search}
                setSearch={setSearch}
                isExport={false}
                defaultSort={{ defaultSortColumn: "created_at", defaultSortAsc: false }}
            />
            <StandardModal
                show={Boolean(acknowledgingMismatch)}
                onHide={closeModal}
                title={`Acknowledge Tag Mismatch: ${acknowledgingMismatch?.box_code || ""}`}
                icon={<i className="fal fa-check" />}
                onSave={handleAcknowledge}
                saveLabel="Acknowledge"
                saveLoading={isSaving}
            >
                <ResizableTextarea
                    id="ohsMismatchNote"
                    label="Note"
                    required
                    rows={4}
                    placeholder="What happened - e.g. tag replaced after an incident, data capture error"
                    value={note}
                    onChange={(event) => {
                        setNote(event.target.value);
                        setNoteError("");
                    }}
                    errorText={noteError}
                />
            </StandardModal>
        </SectionCard>
    );
}

export default TagMismatches;
